/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import {
  HomeOutlined,
  InfoCircleOutlined,
  RightOutlined,
} from '@ant-design/icons'
import Text from '../ts-components/Text'
import Icon from '../Icon'
import useWindowSize from 'src/hooks/useWindowSize'
import { useMemo } from 'react'
import { ROUTES } from '@sportninja/common/constants/app'
import { Link } from 'react-router-dom'
import { formatShortDate } from '@sportninja/common/ts-utils/date-utils'
import dayjs from 'dayjs'

// const getPlaceholderImage = ({
//   isSchedule,
//   isTeam,
//   isOrganization,
// }: {
//   isSchedule: boolean
//   isTeam: boolean
//   isOrganization: boolean
// }) => {
//   if (isSchedule) {
//     return '/images/generic_placeholders/placeholder_competition.png'
//   } else if (isTeam) {
//     return '/images/generic_placeholders/placeholder_team.png'
//   } else if (isOrganization) {
//     return '/images/generic_placeholders/placeholder_organization.png'
//   } else {
//     return '/images/generic_placeholders/placeholder_competition.png'
//   }
// }

const getImageSrc = ({
  imageId,
  isSchedule,
  isTeam,
  isOrganization,
}: {
  imageId: string
  isSchedule: boolean
  isTeam: boolean
  isOrganization: boolean
}) => {
  if (imageId) {
    return imageId
  } else if (isSchedule) {
    return '/images/generic_placeholders/placeholder_competition.png'
  } else if (isTeam) {
    return '/images/generic_placeholders/placeholder_team.png'
  } else if (isOrganization) {
    return '/images/generic_placeholders/placeholder_organization.png'
  } else {
    return '/images/generic_placeholders/placeholder_competition.png'
  }
}

const getBackgroundImage = ({
  genericSportForGame,
  isSoccer,
  isDesktop,
  sport,
  isOrganization,
}: {
  genericSportForGame: any
  isSoccer: boolean
  isDesktop: boolean
  sport: any
  isOrganization: boolean
}) => {
  if (isOrganization) {
    return null
  }
  if (!genericSportForGame) {
    if (isSoccer) {
      return '/images/soccer_game_background'
    } else {
      if (sport && sport?.name) {
        if (sport?.name === 'lacrosse') {
          return '/images/grass'
        }
        if (sport?.name === 'fieldhockey') {
          return '/images/grass'
        }
        if (sport?.name === 'floorball') {
          return '/images/floorball'
        }
        if (sport?.name === 'ballhockey') {
          return '/images/ballhockey'
        }
      }
      return '/images/game_hockey'
    }
  } else {
    return isDesktop
      ? genericSportForGame?.background_web_image?.replace('.png', '')
      : // : genericSportForGame?.background_mobile_image?.replace('.png', '')
        genericSportForGame?.background_web_image?.replace('.png', '')
  }
}

const isContactInfoAvailable = (contactInfo: any) => {
  return (
    contactInfo?.support_phone ||
    contactInfo?.support_email ||
    contactInfo?.support_website ||
    contactInfo?.phone ||
    contactInfo?.email ||
    contactInfo?.website
  )
}

type PageHeaderProps = {
  isSchedule: boolean
  isTeamOrSchedule: boolean
  isOrganization: boolean
  isTeam: boolean
  /** Renders a calendar glyph instead of an entity image. Events carry no
   *  image, and the default fallback is the competition trophy. */
  isEvent?: boolean
  imageId: string
  breadcrumbs: any
  /**
   * Use each crumb's OWN `to` path instead of rebuilding every link as
   * `/org/{id}`.
   *
   * OFF by default: the Org, Team and Schedule pages build their trails with
   * `getBreadcrumbs`, which derives every crumb from ONE root, so only their
   * org crumb has a correct path — the long-standing `/org/{id}` rebuild is
   * what makes that work. Pages that build per-crumb paths themselves (the
   * event page) opt in.
   */
  useCrumbPaths?: boolean
  title: string
  titleActions: any
  /**
   * Aligns `titleActions` to the TOP of the title rather than its baseline.
   *
   * The row is `align-items: flex-end` by default, which is right for the
   * full-height controls Org/Team/Schedule put there (Favourite, Edit) — they
   * should sit on the title's baseline. A short pill (the event page's BETA
   * badge) instead reads as hanging off the bottom of a very large title, so
   * that page opts in. OFF by default, so existing call sites are unchanged.
   */
  titleActionsAlignTop?: boolean
  /**
   * Rendered on the RIGHT of the header row, opposite the title — the slot the
   * Contact Info button uses. `titleActions` differs: it renders INLINE beside
   * the title text.
   */
  headerActions?: React.ReactNode
  subTitle: string
  history?: any
  setIsContactInfoModalOpen: any
  baseCrumb?: string
  baseRoute?: string
  backgroundImageUrl: string
  genericSport: any
  isSoccer: boolean
  sport: any
  contactInfo: {
    support_phone: string
    support_email: string
    support_website: string
    phone: string
    email: string
    website: string
  }
  competitionStartDate: string | null
  competitionEndDate: string | null
}

/**
 * The 80x80 entity glyph in the header. Events carry no image and would
 * otherwise fall through to the competition trophy, so they get a calendar
 * mark in the same slot instead.
 */
const HeaderGlyph = ({
  isEvent,
  imageId,
  isSchedule,
  isTeam,
  isOrganization,
}: {
  isEvent: boolean
  imageId: string
  isSchedule: boolean
  isTeam: boolean
  isOrganization: boolean
}) => {
  /* Shared frame so both branches occupy the identical slot. `display: flex`
     is inert on the <img> (no children) and only centres the calendar glyph;
     `flex-shrink: 0` keeps the 80px against the header's flex row. Existing
     pages render exactly as before. */
  const frameStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    flex-shrink: 0;
    border-radius: 8px;
    border: 1px solid ${colors.SECONDARY_300};
  `

  if (isEvent) {
    // Bare glyph — no frame or fill, unlike entity images which are photos
    // that need a card to sit on.
    return (
      <div
        css={[
          frameStyles,
          css`
            border: none;
          `,
        ]}
      >
        {/* Sized to fill the 80px frame rather than sit inside it. At 56px the
            glyph left ~12px of slack each side, so the visible calendar mark
            started right of the page content below it — the frame was aligned,
            the artwork was not. Entity pages fill the same frame with an 80px
            photo, so matching that puts every header on one left edge.

            72 rather than 80: `Icon` sets only font-size, and a glyph's drawn
            width is slightly under its em box, so 80 would risk overflowing a
            frame that does not clip. */}
        <Icon name='calendar-alt' fontSize={72} color={colors.PRIMARY} />
      </div>
    )
  }

  return (
    <img
      src={getImageSrc({ imageId, isSchedule, isTeam, isOrganization })}
      alt='Page Header'
      width={80}
      height={80}
      css={[
        frameStyles,
        css`
          background-color: ${colors.NEUTRAL_0};
          object-fit: contain;
          max-width: 100%;
          max-height: 100%;
        `,
      ]}
    />
  )
}

const hasCompetitionEnded = (competitionEndDate: string | null) => {
  if (!competitionEndDate) {
    return false
  }
  return dayjs(competitionEndDate).isBefore(dayjs())
}

const PageHeaderWithBackground = ({
  isSchedule,
  isOrganization,
  isTeam = false,
  isEvent = false,
  imageId,
  breadcrumbs,
  useCrumbPaths = false,
  title,
  titleActions,
  titleActionsAlignTop = false,
  headerActions,
  subTitle,
  setIsContactInfoModalOpen,
  // baseCrumb,
  // baseRoute,
  backgroundImageUrl,
  genericSport,
  isSoccer,
  sport,
  contactInfo,
  competitionStartDate = null,
  competitionEndDate = null,
}: PageHeaderProps) => {
  const { width } = useWindowSize()

  const isDesktop = useMemo(() => {
    return width > 768
  }, [width])

  const backgroundImage = getBackgroundImage({
    genericSportForGame: genericSport,
    isSoccer,
    isDesktop,
    sport,
    isOrganization,
  })
  const url = isSchedule
    ? backgroundImageUrl || `${backgroundImage}.png`
    : backgroundImageUrl || imageId || `${backgroundImage}.png`
  const isContactAvailable = isContactInfoAvailable(contactInfo)

  if (!isDesktop) {
    return (
      <div
        css={css`
          display: flex;
          padding: 80px 0px 24px 0px;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 8px;
          width: 100%;
          background: ${colors.NEUTRAL_900};
          min-height: 250px;
          position: relative;
          z-index: 1;
        `}
      >
        <div
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(
                180deg,
                rgba(0, 0, 0, 0.7) 0%,
                rgba(0, 0, 0, 0.7) 100%
              ),
              no-repeat center center / cover url(${url});
            filter: blur(4px);
            z-index: -1;
            border-bottom: 1px solid ${colors.SECONDARY_200};
          `}
        />
        <HeaderGlyph
          isEvent={isEvent}
          imageId={imageId}
          isSchedule={isSchedule}
          isTeam={isTeam}
          isOrganization={isOrganization}
        />
        <Text variant='heading4' color={colors.NEUTRAL_0} weight='semiBold'>
          {title}
        </Text>
        {subTitle && (
          <Text
            variant='captionSmall'
            color={colors.NEUTRAL_100}
            weight='semiBold'
          >
            {subTitle}
          </Text>
        )}
        {isTeam && competitionStartDate && competitionEndDate && (
          <Text
            variant='captionSmall'
            color={colors.NEUTRAL_100}
            weight='semiBold'
          >
            {formatShortDate(competitionStartDate, true)} -{' '}
            {formatShortDate(competitionEndDate, true)} (
            {hasCompetitionEnded(competitionEndDate) ? ' (Ended)' : ''})
          </Text>
        )}
        {headerActions ? (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              gap: 8px;
            `}
          >
            {headerActions}
          </div>
        ) : null}
        {isContactAvailable ? (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              gap: 8px;
              cursor: pointer;
            `}
            onClick={() => {
              setIsContactInfoModalOpen(true)
            }}
          >
            <Text
              variant='paragraphSmall'
              color={colors.PRIMARY}
              weight='regular'
            >
              Contact Info
            </Text>
            <InfoCircleOutlined
              color={colors.PRIMARY}
              width={16}
              height={16}
              css={css`
                font-size: 16px;
                color: ${colors.PRIMARY};
              `}
            />
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div
      css={css`
        display: flex;
        padding: 0px 88px;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        align-self: stretch;
        border-radius: 8px 8px 0px 0px;
        border-bottom: 1px solid ${colors.SOFT_STEEL};
        background: ${colors.NEUTRAL_900};
        height: 220px;
        position: relative;
        z-index: 1;
      `}
    >
      <div
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: -1;
        `}
      >
        <div
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(
                180deg,
                rgba(0, 0, 0, 0.7) 0%,
                rgba(0, 0, 0, 0.7) 100%
              ),
              no-repeat center center / cover url(${url});
            filter: blur(4px);
            z-index: -1;
            border-bottom: 1px solid ${colors.NEUTRAL_100};
          `}
        />
      </div>
      <div
        css={css`
          max-width: 1424px;
          margin: 0px auto;
          width: 100%;
          align-self: stretch;
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
          `}
        >
          <HeaderGlyph
            isEvent={isEvent}
            imageId={imageId}
            isSchedule={isSchedule}
            isTeam={isTeam}
            isOrganization={isOrganization}
          />
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              gap: 8px;
            `}
          >
            {breadcrumbs && breadcrumbs?.length > 0 ? (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                `}
              >
                {/* first breadcrumb is always to the home page */}
                <Link to={ROUTES.HOME}>
                  <HomeOutlined
                    width={16}
                    height={16}
                    color={colors.NEUTRAL_100}
                    css={css`
                      font-size: 16px;
                      color: ${colors.PRIMARY};
                    `}
                  />
                </Link>
                {breadcrumbs?.map((breadcrumb, idx) => {
                  const isLast = idx === breadcrumbs.length - 1
                  return (
                    <Link
                      key={breadcrumb?.id}
                      to={
                        !breadcrumb?.to
                          ? ''
                          : useCrumbPaths
                          ? breadcrumb.to
                          : `${ROUTES.ORG_ROOT}/${breadcrumb?.id}`
                      }
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        pointer-events: ${!breadcrumb?.to ? 'none' : 'auto'};
                        opacity: ${!breadcrumb?.to ? 0.9 : 1};
                      `}
                    >
                      <RightOutlined
                        color={colors.NEUTRAL_100}
                        width={10}
                        height={10}
                        css={css`
                          font-size: 10px;
                          color: ${colors.NEUTRAL_100};
                        `}
                      />
                      <Text
                        variant='captionSmall'
                        color={isLast ? colors.NEUTRAL_100 : colors.PRIMARY}
                        weight='medium'
                      >
                        {breadcrumb?.text || ''}
                      </Text>
                    </Link>
                  )
                })}
              </div>
            ) : null}
            <div
              css={css`
                display: flex;
                align-items: flex-end;
                gap: 8px;
              `}
            >
              <Text
                variant='displaySmall'
                color={colors.NEUTRAL_0}
                weight='semiBold'
              >
                {title}
              </Text>

              {titleActions ? (
                <div
                  css={css`
                    display: flex;
                    padding: 8px 0px;
                    align-items: center;
                    gap: 8px;
                    /* A short pill beside a 48px title.

                       Pinned to the row's top, then nudged down by the empty
                       space the title's font leaves above its capitals: the
                       line box is 48px (line-height 100%) but the visible cap
                       starts roughly a quarter of that below its top, so a
                       plain flex-start left the badge floating clear of the
                       text. 10px is that gap for displaySmall (48px,
                       line-height 100%) in the title face — a literal, because
                       this wrapper does not inherit the title's font-size, and
                       setting one here purely to make an em unit resolve would
                       leak into the badge's own type. */
                    ${titleActionsAlignTop &&
                    `
                      align-self: flex-start;
                      padding: 0;
                      margin-top: 10px;
                    `}
                  `}
                >
                  {titleActions}
                </div>
              ) : null}
            </div>
            {!isTeam && subTitle && (
              <Text
                variant='captionSmall'
                color={colors.NEUTRAL_100}
                weight='semiBold'
              >
                {subTitle}
              </Text>
            )}
            {isTeam && competitionStartDate && competitionEndDate && (
              <Text
                variant='captionSmall'
                color={colors.NEUTRAL_100}
                weight='semiBold'
              >
                {formatShortDate(competitionStartDate, true)} -{' '}
                {formatShortDate(competitionEndDate, true)}
                {hasCompetitionEnded(competitionEndDate) ? ' (Ended)' : ''}
              </Text>
            )}
            {isTeam && subTitle && (
              <div
                css={css`
                  display: flex;
                  align-items: flex-start;
                  justify-content: center;
                  gap: 8px;
                `}
              >
                <Text
                  variant='captionSmall'
                  color={colors.NEUTRAL_100}
                  weight='semiBold'
                >
                  {subTitle}
                </Text>
              </div>
            )}
          </div>
        </div>
        {headerActions ? (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 12px;
            `}
          >
            {headerActions}
          </div>
        ) : null}
        {isContactAvailable ? (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-end;
              gap: 8px;
            `}
          >
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                gap: 8px;
                align-self: stretch;
                cursor: pointer;
                border: 1px solid ${colors.PRIMARY};
                padding: 8px;
                border-radius: 8px;
              `}
              onClick={() => {
                setIsContactInfoModalOpen(true)
              }}
            >
              <Text variant='paragraphSmall' color={colors.PRIMARY}>
                Contact Info
              </Text>
              <InfoCircleOutlined
                color={colors.PRIMARY}
                width={16}
                height={16}
                css={css`
                  font-size: 16px;
                  color: ${colors.PRIMARY};
                `}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default PageHeaderWithBackground
